import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StateService } from '@adgate/common/services/state-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MainNotification } from '@adgate/modules/main/types/main-notification.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { environment } from '@env/environment';
import { AssistantDialogComponent } from '@adgate/common/components/assistant-dialog-component/assistant-dialog-component';
import { ModuleActivationService } from '@adgate/common/services/module-activation-service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-main-frame-component',
  imports: [CommonModule, RouterOutlet, MatTooltipModule, MatFormFieldModule, MatInputModule],
  templateUrl: './main-frame-component.html',
  styleUrl: './main-frame-component.scss',
})
export class MainFrameComponent implements OnInit
{
  @ViewChild('searchBox') searchBox!: ElementRef<HTMLInputElement>;

  public AdGateConstants = AdGateConstants;
  public stateService: StateService = inject(StateService);
  public authService: AuthenticationService = inject(AuthenticationService);
  public dialogService: DialogService = inject(DialogService);
  public commonObjectService: CommonObjectService = inject(CommonObjectService);
  public moduleActivationService: ModuleActivationService = inject(ModuleActivationService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  public activeRouteComponent: any;
  public notificationList: MainNotification[] = [];
  public showModule: any = {
    linear: false,
    streaming: false,
    production: false,
    admin: false
  };

  constructor()
  {
    this.stateService.restore();
    this.initPrivileges();
  }

  
  ngOnInit(): void
  {
    this.dialogService.showProgress();
    this.commonObjectService.getMainNotifications().pipe(
      finalize(() => this.dialogService.hideProgress())
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.notificationList = r.data;
        this.cdr.detectChanges();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  initPrivileges()
  {
    let decodedToken = this.authService.decodeToken( this.stateService.data.userToken );
    this.showModule.admin = this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.ADMI_CREATEANYUSER,Privilege.ADMI_CREATEOFFICEUSERS]) ||
                          this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.ADMI_CREATEROLE]) ||
                          this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.ADMI_EDITOFFICES]) ||
                          this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.ADMI_EDITAGENCIES,Privilege.ADMI_EDITOFFICEAGENCIES]);

    this.showModule.linear = this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.COMM_VIEWHISTORY,Privilege.COMM_UPDATECOMMERCIAL,Privilege.COMM_UPLOADCOMMERCIAL]);
    this.showModule.streaming = this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.STRM_UPDATEORDER,Privilege.STRM_UPDATECREATIVITY,Privilege.STRM_UPLOADCREATIVITY,Privilege.STRM_VIEWHISTORY]);
    this.showModule.production = this.authService.checkPrivilegeOnToken(decodedToken,[Privilege.PROD_UPDATEORDER,Privilege.PROD_UPDATEVERSION,Privilege.PROD_VIEWHISTORY]);
  }

  onActivateRoute(component: any): void
  {
    this.activeRouteComponent = component;
  }

  onOpenAssistant()
  {
    this.dialogService.openAnyDialog(AssistantDialogComponent, {
      width: '600px',    
    });
  }

  onOpenHelpCenter(): void
  {
    window.open(environment.helpCenterUrl, '_blank');
  }

  onSignOut(): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Do you really want to leave?`,
      {
        title: 'Sign out',
        confirmText: 'Sign out',
        cancelText: 'Cancel',
        severity: 'info'
      }
    );

    dialogRef.afterClosed().subscribe(confirmed => {
      if( confirmed ) {
        this.authService.signOut();
      }
    });
  }

  onActivateModule(moduleId: number): void
  {

  }

  onChangeTab(tabNum: number)
  {
    this.searchBox.nativeElement.value = '';
    this.activeRouteComponent.onChangeTab(tabNum);
  }

  decodeNotificationColor(n: MainNotification): string
  {
    switch(n.importance) {
      case 1: return "#10B981"; break;
      case 2: return "#F59E0B"; break;
      case 3: return "#EF4444"; break;
      default: return "#FFFFFF";
    }
  }

  onFilter()
  {
    if( this.activeRouteComponent != null ) {
      this.activeRouteComponent.onFilter();
    }
  }

  onRefresh()
  {
    if( this.activeRouteComponent != null ) {
      this.activeRouteComponent.onRefresh();
    }
  }

  onExport()
  {
    if( this.activeRouteComponent != null ) {
      this.activeRouteComponent.onExport();
    }
  }

  onQuickSearch(inputElement: EventTarget | null)
  {
    if( this.activeRouteComponent != null ) {
      this.activeRouteComponent.onQuickSearch((<HTMLInputElement>inputElement).value.trim().toLowerCase());
    }
  }

  onShowUserProfile()
  {
    this.dialogService.openUserProfileDialog();
  }

  getQuickSearchPlaceholder(): string
  {
    try {
      return this.activeRouteComponent.getQuickSearchPlaceholder();
    } catch {
      return '';
    }
  }

  showQuickSearch(): boolean
  {
    return this.activeRouteComponent != null &&
           typeof this.activeRouteComponent.onQuickSearch === 'function';
  }

  showExport(): boolean
  {
    return this.activeRouteComponent != null &&
           typeof this.activeRouteComponent.onExport === 'function';
  }

  showFilter(): boolean
  {
    return this.activeRouteComponent != null &&
           typeof this.activeRouteComponent.showFilters === 'function' &&
           this.activeRouteComponent.showFilters();
  }

  showTabs(): boolean
  {
    return this.activeRouteComponent != null &&
           typeof this.activeRouteComponent.getTabs === 'function';
  }
}
